import React, { useState } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Lozenge } from '../../../CommonComponents/OPL/Lozenge/Lozenge';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import {
  CategoryDef,
  countImportedOnlyRows,
  countMatchedBulkRows,
  isActionable,
  pluralizeEntity,
  sectionDifferenceCount,
  setImportedOnlyDecision,
  setMatchedDecision,
} from '../meta';
import { CategorySection, Decisions, DifferenceDecision } from '../pdfReconciliationTypes';
import { DifferenceRow } from './DifferenceRow';

interface Props {
  def: CategoryDef;
  section: CategorySection;
  decisions: Decisions;
  onChange: (next: Decisions) => void;
}

const DifferencesSummary = ({
  section,
  decisions,
}: {
  section: CategorySection;
  decisions: Decisions;
}) => {
  const count = sectionDifferenceCount(section, decisions);

  if (count === 0) {
    return <Lozenge variantColor="success">No new items</Lozenge>;
  }

  return <Lozenge variantColor="new">{`${count} new item${count > 1 ? 's' : ''}`}</Lozenge>;
};

interface BulkActionBarProps {
  def: CategoryDef;
  section: CategorySection;
  decisions: Decisions;
  onChange: (next: Decisions) => void;
}

const BulkActionBar = ({ def, section, decisions, onChange }: BulkActionBarProps) => {
  const importedOnlyCount = countImportedOnlyRows(section);
  const matchedDiscardCount = countMatchedBulkRows(section);
  const matchedReplaceCount = countMatchedBulkRows(section, { overwriteOnly: true });

  const hasImportedOnly = importedOnlyCount > 0;
  const hasMatched = matchedDiscardCount > 0;

  if (!hasImportedOnly && !hasMatched) {
    return null;
  }

  const newLabel = pluralizeEntity(def, importedOnlyCount);
  const matchedDiscardLabel = pluralizeEntity(def, matchedDiscardCount);
  const matchedReplaceLabel = pluralizeEntity(def, matchedReplaceCount);

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      css={({ theme }) => ({ gap: theme.space[12] })}
      mb="16"
    >
      <Box flex={1} minWidth={0}>
        <Box
          display="grid"
          css={({ theme }) => ({
            gridTemplateColumns: '1fr 1fr',
            gap: theme.space[24],
          })}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            css={({ theme }) => ({ gap: theme.space[8] })}
          >
            {hasImportedOnly && (
              <Button
                size="small"
                variant="subtleActive"
                onClick={() => onChange(setImportedOnlyDecision(decisions, section, 'Add'))}
              >
                {`Add ${importedOnlyCount} new ${newLabel}`}
              </Button>
            )}

            {matchedReplaceCount > 0 && (
              <Button
                size="small"
                variant="subtleActive"
                onClick={() => onChange(setMatchedDecision(decisions, section, 'Overwrite'))}
              >
                {`Replace ${matchedReplaceCount} existing ${matchedReplaceLabel}`}
              </Button>
            )}
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            css={({ theme }) => ({ gap: theme.space[8] })}
          >
            {hasImportedOnly && (
              <Button
                size="small"
                variant="subtleInactive"
                onClick={() => onChange(setImportedOnlyDecision(decisions, section, 'Discard'))}
              >
                {`Discard ${importedOnlyCount} new ${newLabel}`}
              </Button>
            )}

            {hasMatched && (
              <Button
                size="small"
                variant="subtleInactive"
                onClick={() => onChange(setMatchedDecision(decisions, section, 'Discard'))}
              >
                {`Discard ${matchedDiscardCount} imported ${matchedDiscardLabel}`}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box flexShrink={0} width="11.5rem" />
    </Box>
  );
};

export const CategoryAccordion = ({ def, section, decisions, onChange }: Props) => {
  const hasActionableRow = section.rows.some(isActionable);
  const [isOpen, setIsOpen] = useState(hasActionableRow);

  const sectionDecisions = decisions[section.categoryKey] || {};
  const handleRowChange = (rowId: string, decision: DifferenceDecision) => {
    onChange({
      ...decisions,
      [section.categoryKey]: { ...sectionDecisions, [rowId]: decision },
    });
  };

  return (
    <Box border="1px solid" borderColor="neutral.200" borderRadius="medium" mb="12" bg="bg">
      <Box
        onClick={() => setIsOpen(o => !o)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        p="16"
        bg="neutral.50"
        borderRadius="medium"
        style={{ cursor: 'pointer' }}
        role="button"
        aria-expanded={isOpen}
      >
        <Box>
          <Text fontWeight={600} color="neutral.700">
            {def.label}
          </Text>
          <Text fontSize="small" color="neutral.500">
            {`${section.existingCount} in existing plan \u2022 ${section.importedCount} in import`}
          </Text>
        </Box>
        <Box display="flex" alignItems="center" css={({ theme }) => ({ gap: theme.space[12] })}>
          <DifferencesSummary section={section} decisions={decisions} />
          <Icon
            className={isOpen ? 'far fa-chevron-up' : 'far fa-chevron-down'}
            color="neutral.500"
            fontSize="small"
          />
        </Box>
      </Box>

      {isOpen && (
        <Box p="16">
          {!def.isSingleton && hasActionableRow && (
            <BulkActionBar def={def} section={section} decisions={decisions} onChange={onChange} />
          )}

          {section.rows.length === 0 && (
            <Box py="16">
              <Text color="neutral.500">No items to reconcile.</Text>
            </Box>
          )}

          {section.rows.map(row => (
            <DifferenceRow
              key={row.rowId}
              row={row}
              entitySingular={def.entitySingular}
              decision={sectionDecisions[row.rowId]}
              onChange={d => handleRowChange(row.rowId, d)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
